import { Button, Logo, Reveal, SessionContext, Stack, Text, useT } from "@bundle/ui";
import { useContext, useEffect, useState } from "react";

/** How long the loading screen waits before it offers Try again / Sign out (Slack's "trouble loading" screen). */
export const LOADING_TIMEOUT_MS = 15_000;

/**
 * First launch while the session or the workspace list loads (never shown when there's cached content). It never
 * spins forever: past LOADING_TIMEOUT_MS it says so and offers Try again (a reload: a fresh session check and the
 * latest build) and, signed in, Sign out.
 */
export function LoadingScreen({ timeoutMs = LOADING_TIMEOUT_MS }: { timeoutMs?: number }) {
  const t = useT();
  // Outside the session (the gallery, the desktop call window) there's nothing to sign out of.
  const session = useContext(SessionContext);
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setStuck(true), timeoutMs);
    return () => clearTimeout(timer);
  }, [timeoutMs]);
  const signOut = () => {
    session?.signOut();
    location.assign("/");
  };
  return (
    <Stack grow align="center" justify="center" gap="4" background="bg.window" testID="loading-screen">
      <Logo variant="symbol" size="avatar.lg" />
      <Text color="text.secondary">{t("workspace.loading")}</Text>
      <Reveal shown={stuck} testID="loading-stuck">
        <Stack align="center" gap="3">
          <Stack align="center" gap="1">
            <Text weight="semibold" align="center">
              {t("workspace.loadingStuck.title")}
            </Text>
            <Text color="text.secondary" align="center">
              {t("workspace.loadingStuck.body")}
            </Text>
          </Stack>
          <Stack direction="horizontal" gap="2" justify="center">
            <Button variant="primary" label={t("common.retry")} onPress={() => location.reload()} testID="loading-retry" />
            {session === null ? null : <Button label={t("auth.signOut")} onPress={signOut} testID="loading-sign-out" />}
          </Stack>
        </Stack>
      </Reveal>
    </Stack>
  );
}
